import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormControlLabel, Grid, MenuItem, Select } from '@mui/material';
import { scaleLinear, scaleBand, scaleOrdinal } from '@visx/scale';
import { BarStackHorizontal } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { max } from 'd3-array';
import PropTypes from 'prop-types';

import {
  getParams,
  getIndexFromObjList,
  thousandFormat,
} from '../../../../Utils/Functions';
import {
  daysRangeKeys,
  debtorKeyTextMap,
  debtorColors,
  barHeight,
  activeLabelColor,
} from '../../../../Assets/js/constant';

const xSelector = (d) => d.Outstanding30Days + d.Outstanding60Days + d.Outstanding90Days + d.Outstanding120Days + d.Outstanding120PlusDays;
const dSelector = (d) => d.Director;
const vSelector = (d) => d.ByValue;
const pSelector = (d) => d.ProjectManager;
const aSelector = (d) => d.Supervisor;
const cSelector = (d) => d.ClientName;
const s1Selector = (d) => d.ProjectSourceOne;
const s2Selector = (d) => d.ProjectSourceTwo;

const getProjectFilterConfig = (projectFilter) => {
  switch (projectFilter) {
    case 'By Value': return { selector: vSelector, field: 'ByValue' };
    case 'Director': return { selector: dSelector, field: 'Director' };
    case 'Project Leader': return { selector: pSelector, field: 'ProjectManager' };
    case 'Associate': return { selector: aSelector, field: 'Supervisor' };
    case 'Project Source 1': return { selector: s1Selector, field: 'ProjectSourceOne' };
    case 'Project Source 2': return { selector: s2Selector, field: 'ProjectSourceTwo' };
    default: return { selector: cSelector, field: 'ClientName' };
  }
};

const ItemsChart = ({
  detailData,
  selectedYears,
  selectedDaysRanges,
  selectedItems,
  selectedItemStacks,
  projectFilter,
  handleFilter,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const isMountedRef = useRef(false);
  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [colors, setColors] = useState([]);
  const [activeStack, setActiveStack] = useState(null);
  const [selectedStacks, setSelectedStacks] = useState([]);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const deSelectAll = useCallback(() => {
    selectedStacks.forEach((selectedStack) => {
      selectedStack.classList.remove('barActive');
    });
    localStorage.removeItem('dtSelectedItems');
    setSelectedStacks([]);
    handleFilter({
      selectedItems: [],
      selectedItemStacks: [],
    });
  }, [selectedStacks, handleFilter]);

  const prepareData = useCallback((projectFilterParam = 'Director') => {
    const keysList =
      selectedDaysRanges.length === 0 ||
      selectedDaysRanges[selectedDaysRanges.length - 1] === 'Total'
        ? daysRangeKeys
        : selectedDaysRanges;

    let colorsList = keysList.reduce((ret, key) => {
      ret.push(debtorColors[key]);
      return ret;
    }, []);

    let dataObj = {};
    const { selector, field } = getProjectFilterConfig(projectFilterParam);

    detailData.forEach((d) => {
      const groupValue = selector(d) || '';
      if (!dataObj[groupValue]) dataObj[groupValue] = {};
      if (!dataObj[groupValue]['Total']) dataObj[groupValue]['Total'] = 0;
      dataObj[groupValue]['Total'] += parseFloat(d.InvoiceAmount);

      keysList.forEach((key) => {
        if (!dataObj[groupValue][key]) dataObj[groupValue][key] = 0;
        dataObj[groupValue][key] += parseFloat(d[key]);
      });
    });

    dataObj = Object.keys(dataObj).map((groupValue) => {
      const d = dataObj[groupValue];
      d[field] = groupValue;
      keysList.forEach((key) => {
        if (!d[key]) d[key] = 0;
      });
      return d;
    });
    dataObj.sort((a, b) =>
      projectFilterParam === 'By Value'
        ? (a.Total > b.Total ? -1 : 1)
        : (a.Total > b.Total ? 1 : -1),
    );

    setData(dataObj);
    setKeys(keysList);
    setColors(colorsList);
  }, [detailData, selectedDaysRanges]);

  const handleStackSelect = useCallback((event, bar) => {
    const { selector } = getProjectFilterConfig(projectFilter);

    const stack = {
      director: selector(bar.bar.data),
      daysRange: bar.key,
    };
    const index = getIndexFromObjList(selectedItemStacks, stack);

    let _selectedItems = [];
    let _selectedItemStacks = [];
    let _selectedStacks = [];

    if (event.shiftKey) {
      _selectedItems = selectedItems.slice();
      _selectedItemStacks = selectedItemStacks.slice();
      _selectedStacks = selectedStacks.slice();

      if (index > -1) {
        event.target.classList.remove('barActive');
        _selectedStacks.splice(index, 1);
        _selectedItemStacks.splice(index, 1);
      } else {
        event.target.classList.add('barActive');
        _selectedStacks.push(event.target);
        _selectedItemStacks.push(stack);
      }
    } else {
      _selectedItems = [];
      selectedStacks.forEach((selectedStack) => {
        selectedStack.classList.remove('barActive');
      });

      if (
        index > -1 &&
        selectedItems.length === 0 &&
        selectedItemStacks.length === 1
      ) {
        _selectedStacks = [];
        _selectedItemStacks = [];
      } else {
        event.target.classList.add('barActive');
        _selectedStacks = [event.target];
        _selectedItemStacks = [stack];
      }
    }

    setSelectedStacks(_selectedStacks);
    handleFilter({
      selectedItems: _selectedItems,
      selectedItemStacks: _selectedItemStacks,
    });
  }, [selectedItems, selectedItemStacks, selectedStacks, projectFilter, handleFilter]);

  const handleLabelSelect = useCallback((event, director) => {
    const index = selectedItems.indexOf(director);

    let _selectedItems = [];
    let _selectedItemStacks = [];
    let _selectedStacks = [];

    if (event.shiftKey) {
      _selectedItems = selectedItems.slice();
      _selectedItemStacks = selectedItemStacks.slice();
      _selectedStacks = selectedStacks.slice();

      if (index > -1) {
        _selectedItems.splice(index, 1);
      } else {
        _selectedItems.push(director);
      }
    } else {
      _selectedItemStacks = [];
      _selectedStacks = [];

      selectedStacks.forEach((selectedStack) => {
        selectedStack.classList.remove('barActive');
      });

      if (
        index > -1 &&
        selectedItems.length === 1 &&
        selectedItemStacks.length === 0
      ) {
        _selectedItems = [];
      } else {
        _selectedItems = [director];
      }
    }

    setSelectedStacks(_selectedStacks);
    handleFilter({
      selectedItems: _selectedItems,
      selectedItemStacks: _selectedItemStacks,
    });
  }, [selectedItems, selectedItemStacks, selectedStacks, handleFilter]);

  const showTooltipHandler = useCallback((event, bar) => {
    if (activeStack) activeStack.classList.remove('barHover');
    event.target.classList.add('barHover');
    setActiveStack(event.target);

    showTooltip({
      tooltipData: bar,
      tooltipTop: event.clientY - 100,
      tooltipLeft: event.clientX - 30,
    });
  }, [activeStack, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (activeStack) activeStack.classList.remove('barHover');
    setActiveStack(null);
    hideTooltip();
  }, [activeStack, hideTooltip]);

  const handleChange = useCallback(async (event) => {
    await handleFilter({
      projectFilter: event.target.value,
    });
    prepareData(event.target.value);
  }, [handleFilter, prepareData]);

  // Mount effect
  useEffect(() => {
    isMountedRef.current = true;
    prepareData(projectFilter);
    window.addEventListener('resize', onResize);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [projectFilter, onResize]);

  // Update effect - using JSON.stringify for array comparison and specific length checks
  useEffect(() => {
    if (isMountedRef.current) {
      deSelectAll();
      prepareData(projectFilter);
    }
  }, [detailData.length, selectedYears.length, JSON.stringify(selectedDaysRanges)]);

  const { selector } = getProjectFilterConfig(projectFilter);

  let yDomains = data.map(selector);
  let yLengths = [];
  yDomains.map((data) =>
    data ? yLengths.push(data.length) : yLengths.push(0),
  );
  let maxYLength = Math.max(
    Math.abs(Math.min(...yLengths)),
    Math.max(...yLengths),
  );
  let xTransform =
    projectFilter === 'By Value'
      ? 7.5 * (maxYLength + 1) + 10
      : 7.5 * maxYLength + 10;

  const width = window.innerWidth / 2 - 8;
  const height =
    data.length > 5 ? 29 * data.length + 30 : window.innerHeight / 6;
  const margin = {
    top: 10,
    right: 30,
    bottom: 20,
    left: yLengths.length > 0 ? xTransform : 10,
  };
  const { xMax, yMax } = getParams(window, width, height, margin);

  const xScale = scaleLinear({
    range: [0, xMax],
    domain: [0, max(data.map(xSelector)) * 1.1],
    nice: true,
  });

  const yScale = scaleBand()      
    .domain(data.map(selector))
    .padding(0.2)
    .rangeRound([yMax, yMax - barHeight * data.length]);

  const color = scaleOrdinal({
    domain: keys,
    range: colors,
  });

  let tooltipTimeout;

  return (
    <div>
      <div className="subtitle mb-10">
        <FormControlLabel
          control={
            <Select
              onChange={handleChange}
              sx={{
                '& .MuiInputBase-input': {
                  padding: 0,
                  paddingRight: 24,
                  fontWeight: 'bold',
                  fontFamily: 'Verdana !important',
                  fontSize: '10pt',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 0,
                },
              }}
              value={projectFilter}
            >
              <MenuItem value="Director">Director</MenuItem>
              <MenuItem value="By Value">By Value</MenuItem>
              <MenuItem value="Project Leader">Project Leader</MenuItem>
              <MenuItem value="Associate">Associate</MenuItem>
              <MenuItem value="Project Source 1">Project Source 1</MenuItem>
              <MenuItem value="Project Source 2">Project Source 2</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
            </Select>
          }
        />
      </div>

      <Grid
        container
        style={{ maxHeight: window.innerHeight / 6 - 21, overflow: 'hidden auto' }}
      >
        <svg width={width} height={height}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
            onClick={deSelectAll}
          />

          {data.map((d, index) => {
            return (
              <rect
                key={index}
                x={0}
                y={yScale(selector(d)) + 8}
                width={margin.left}
                height={barHeight}
                fill={
                  selectedItems.indexOf(selector(d)) > -1
                    ? activeLabelColor
                    : 'transparent'
                }
                onClick={(event) => {
                  handleLabelSelect(event, selector(d));
                }}
              />
            );
          })}

          <Group top={margin.top} left={margin.left}>
            <AxisLeft
              hideTicks={true}
              scale={yScale}
              tickValues={data.map(selector)}
              numTicks={data.length}
              tickStroke="black"
              stroke="black"
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  fill={
                    selectedItems.indexOf(formattedValue) > -1
                      ? 'white'
                      : 'black'
                  }
                  onClick={(event) =>
                    handleLabelSelect(event, formattedValue)
                  }
                >
                  {projectFilter === 'By Value'
                    ? `$${thousandFormat(formattedValue)}`
                    : formattedValue}
                </text>
              )}
            />

            <BarStackHorizontal
              data={data}
              keys={keys}
              height={yMax}
              y={selector}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`barstack-horizontal-${barStack.index}-${bar.index}`}
                        x={bar.x + 1}
                        y={bar.y}
                        width={isNaN(bar.width) ? 0 : bar.width}
                        height={isNaN(bar.height) ? 0 : bar.height}
                        fill={bar.color}
                        onClick={(event) => {
                          handleStackSelect(event, bar);
                        }}
                        onMouseLeave={(event) => {
                          tooltipTimeout = setTimeout(() => {
                            hideTooltipHandler();
                          }, 300);
                        }}
                        onMouseMove={(event) => {
                          if (tooltipTimeout) clearTimeout(tooltipTimeout);
                          showTooltipHandler(event, bar);
                        }}
                        onTouchEnd={(event) => {
                          tooltipTimeout = setTimeout(() => {
                            hideTooltipHandler();
                          }, 300);
                        }}
                        onTouchMove={(event) => {
                          if (tooltipTimeout) clearTimeout(tooltipTimeout);
                          showTooltipHandler(event, bar);
                        }}
                      />
                    );
                  });
                });
              }}
            </BarStackHorizontal>
          </Group>
        </svg>

        {tooltipOpen && (
          <TooltipWithBounds
           
            top={tooltipTop + margin.top + margin.bottom}
            left={tooltipLeft}
          >
            <div className="bkg-white color-black" style={{ minWidth: 60 }}>
              <p className="tooltipPanel">
                <strong>{selector(tooltipData.bar.data)}</strong>
              </p>
              <p className="tooltipPanel">{`${
                debtorKeyTextMap[tooltipData.key]
              } days`}</p>
              <p className="tooltipPanel">{`$${thousandFormat(
                tooltipData.bar.data[tooltipData.key],
              )}`}</p>
            </div>
          </TooltipWithBounds>
        )}
      </Grid>
      <svg width={width} height={21}>
        <Group top={1} left={margin.left}>
          <AxisBottom
            scale={xScale}
            top={0}
            numTicks={6}
            tickStroke="black"
            stroke="black"
            tickFormat={(value, index) => value / 1000}
            tickLabelProps={(value, index) => ({
              fill: 'black',
              fontSize: 11,
              textAnchor: 'middle',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text {...tickProps}>
                $
                {Math.abs(formattedValue) < 1000
                  ? `${formattedValue}K`
                  : `${formattedValue / 1000}M`}
              </text>
            )}
          />
        </Group>
      </svg>
    </div>
  );
};

ItemsChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedDaysRanges: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  selectedItemStacks: PropTypes.array.isRequired,
  projectFilter: PropTypes.string.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default ItemsChart;
